"use client"

import * as React from "react"
import { Slider as SliderPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"
import useStore from "@/stores/useStore"

function SliderBase({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  "aria-label": ariaLabel,
  ...props
}) {
  const _values = React.useMemo(
    () => (value !== undefined ? (Array.isArray(value) ? value : [value]) : (Array.isArray(defaultValue) ? defaultValue : [defaultValue ?? min])),
    [value, defaultValue, min]
  )

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={Array.isArray(defaultValue) ? defaultValue : [defaultValue]}
      value={Array.isArray(value) ? value : [value]}
      min={min}
      max={max}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=horizontal]:h-5 data-[orientation=vertical]:w-5 data-[orientation=vertical]:flex-col",
        className
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className="bg-border/60 relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5"
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className="bg-primary absolute rounded-full data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full"
        />
      </SliderPrimitive.Track>
      {_values.map((_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={index}
          aria-label={ariaLabel}
          className="border-primary bg-background block size-4 shrink-0 rounded-full border shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none"
        />
      ))}
    </SliderPrimitive.Root>
  )
}

/**
 * Slider with deferred commit and inline editable input.
 * The slider is the primary interaction; the input is a small secondary way to type exact values.
 */
function Slider({ value, onValueCommit, onValueChange, min = 0, max = 100, inputMax, rangeHint = false, step = 0.25, suffix = "%", label, ariaLabel, className, validationId, validationLabel, validationTarget, validationActive = true, ...props }) {
  // The track covers the common range; `inputMax` lets the typed input accept
  // larger (legal but unusual) values without stretching the track.
  const typedMax = inputMax ?? max
  // When `label` is a node (e.g. carries an inline delta), fall back to `ariaLabel`.
  const resolvedAriaLabel = typeof label === 'string' ? label : ariaLabel
  const generatedId = React.useId()
  const inputErrorId = validationId || resolvedAriaLabel || generatedId
  const inputErrorLabel = validationLabel || resolvedAriaLabel || 'Slider input'
  const setInputError = useStore((state) => state.setInputError)
  const storedInputError = useStore((state) => state.inputErrors[inputErrorId])
  const inputValidationResetKey = useStore((state) => state.inputValidationResetKey)
  const lastValidationResetKey = React.useRef(inputValidationResetKey)
  const previousStoredMessageRef = React.useRef('')
  const skipNextErrorPublishRef = React.useRef(false)
  const skipNextBlurCommitRef = React.useRef(false)
  const [localValue, setLocalValue] = React.useState(value)
  const [inputValue, setInputValue] = React.useState(String(value))
  const [editing, setEditing] = React.useState(false)
  const [error, setError] = React.useState('')
  const [blockingError, setBlockingError] = React.useState('')
  const storedMessage = validationActive && !props.disabled ? storedInputError?.message : ''
  const storedDraft = storedInputError?.draft !== undefined ? String(storedInputError.draft) : null
  const activeError = validationActive && !props.disabled
    ? (blockingError || storedMessage || '')
    : ''
  const displayedInputValue = !editing && !blockingError && storedMessage && storedDraft !== null
    ? storedDraft
    : inputValue
  const isInvalid = Boolean(activeError)
  const dragging = React.useRef(false)
  // If a typed value exceeds the track, stretch the track to fit it so the
  // thumb never pins to the end and the value stays draggable.
  const trackMax = Math.max(max, Number.isFinite(localValue) ? localValue : min)

  // The external value is authoritative. During a drag it does not change until
  // commit, so an actual prop change means undo/redo, a preset, or another
  // external update and must replace any stale local drag state.
  React.useEffect(() => {
    setLocalValue(value)
  }, [value])

  React.useEffect(() => {
    if (!editing && !error) {
      setInputValue(String(value))
    }
  }, [value, editing, error])

  React.useLayoutEffect(() => {
    const hadStoredMessage = previousStoredMessageRef.current
    previousStoredMessageRef.current = storedMessage

    if (hadStoredMessage && !storedMessage && blockingError) {
      skipNextErrorPublishRef.current = true
      dragging.current = false
      setLocalValue(value)
      setInputValue(String(value))
      setEditing(false)
      setError('')
      setBlockingError('')
      return
    }

    if (storedMessage && storedDraft !== null && !editing && !blockingError && inputValue !== storedDraft) {
      setInputValue(storedDraft)
    }
  }, [blockingError, editing, inputValue, storedDraft, storedMessage, value])

  React.useEffect(() => {
    if (skipNextErrorPublishRef.current) {
      skipNextErrorPublishRef.current = false
      return undefined
    }
    if (lastValidationResetKey.current !== inputValidationResetKey) return
    if (!validationActive) return undefined
    const message = validationActive && !props.disabled ? blockingError : ''
    if (!message && storedMessage) return undefined
    setInputError(inputErrorId, message
      ? { message, label: inputErrorLabel, target: validationTarget || { validationId: inputErrorId }, draft: inputValue }
      : '')
    return undefined
  }, [blockingError, inputErrorId, inputErrorLabel, inputValidationResetKey, inputValue, props.disabled, setInputError, storedMessage, validationActive, validationTarget])

  React.useEffect(() => () => {
    setInputError(inputErrorId, '', { track: false })
  }, [inputErrorId, setInputError])

  React.useEffect(() => {
    if (lastValidationResetKey.current === inputValidationResetKey) return
    lastValidationResetKey.current = inputValidationResetKey
    dragging.current = false
    setLocalValue(value)
    setInputValue(String(value))
    setEditing(false)
    setError('')
    setBlockingError('')
  }, [inputValidationResetKey, value])

  const validateInput = React.useCallback((raw) => {
    const trimmed = String(raw ?? '').trim()
    if (!trimmed) return 'Enter a number.'

    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed)) return 'Enter a valid number.'
    if (parsed < min) return `Enter ${min} or more.`
    if (parsed > typedMax) return `Enter ${typedMax} or less.`
    return ''
  }, [typedMax, min])

  const handleChange = React.useCallback((val) => {
    dragging.current = true
    setLocalValue(val[0])
    setInputValue(String(val[0]))
    setError('')
    setBlockingError('')
    onValueChange?.(val)
    setInputError(inputErrorId, '', { track: false })
  }, [inputErrorId, onValueChange, setInputError])

  const handleCommit = React.useCallback((val) => {
    dragging.current = false
    setLocalValue(val[0])
    setInputValue(String(val[0]))
    setError('')
    setBlockingError('')
    onValueCommit?.(val)
    setInputError(inputErrorId, '', { track: false })
  }, [inputErrorId, onValueCommit, setInputError])

  const commitInputValue = React.useCallback(() => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false
      return
    }

    setEditing(false)
    const message = validateInput(inputValue)
    if (message) {
      setError(message)
      setBlockingError(message)
      return
    }

    const trimmed = String(inputValue).trim()
    let parsed = Number(trimmed)
    parsed = Math.min(typedMax, Math.max(min, parsed))
    // Snap to step
    parsed = Math.round(parsed / step) * step
    // Fix floating point
    parsed = parseFloat(parsed.toFixed(10))
    setLocalValue(parsed)
    setInputValue(String(parsed))
    setError('')
    setBlockingError('')
    onValueCommit?.([parsed])
    setInputError(inputErrorId, '', { track: false })
  }, [inputErrorId, inputValue, typedMax, min, onValueCommit, setInputError, step, validateInput])

  const handleKeyDown = React.useCallback((e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    } else if (e.key === 'Escape') {
      skipNextBlurCommitRef.current = true
      dragging.current = false
      setLocalValue(value)
      setInputValue(String(value))
      setError('')
      setBlockingError('')
      setInputError(inputErrorId, '', { track: false })
      setEditing(false)
      e.target.blur()
    }
  }, [inputErrorId, setInputError, value])

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <div className="flex items-center justify-between">
          {/* A function label receives the live value so inline annotations (e.g. a
              delta vs. another plan) track the drag without waiting for commit. */}
          <span className="text-sm">{typeof label === 'function' ? label(localValue) : label}</span>
          <div className="flex items-center gap-0.5">
            <input
              type="text"
              inputMode="decimal"
              value={editing || isInvalid ? displayedInputValue : localValue}
              onChange={(e) => {
                setEditing(true)
                setInputValue(e.target.value)
                const message = validateInput(e.target.value)
                setBlockingError(message)
                if (message) {
                  if (error) setError(message)
                  return
                }
                // A valid keystroke commits immediately (no Enter/blur needed) and
                // clears any error published from an earlier invalid draft – otherwise
                // "Enter a number." sticks while the box already holds a good value.
                // Step-snapping waits for blur so typing "2.1" en route to "2.15" works.
                setError('')
                const parsed = Math.min(typedMax, Math.max(min, Number(e.target.value.trim())))
                setLocalValue(parsed)
                onValueCommit?.([parsed])
                setInputError(inputErrorId, '', { track: false })
              }}
              onFocus={(e) => { setEditing(true); e.target.select() }}
              onBlur={commitInputValue}
              onKeyDown={handleKeyDown}
              className={cn(
                "w-14 h-6 text-right text-sm font-medium bg-transparent border-b border-input hover:border-foreground/40 focus:border-primary focus:outline-none px-1 rounded-sm",
                isInvalid && "border-destructive text-destructive focus:border-destructive"
              )}
              aria-label={resolvedAriaLabel}
              aria-invalid={isInvalid ? true : undefined}
              data-validation-id={inputErrorId}
              title={(isInvalid && (error || activeError)) || undefined}
            />
            {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
          </div>
        </div>
      )}
      {isInvalid && (error || activeError) && <p className="-mt-1 text-right text-xs text-destructive">{error || activeError}</p>}
      <div>
        <SliderBase
          aria-label={resolvedAriaLabel}
          value={localValue}
          onValueChange={handleChange}
          onValueCommit={handleCommit}
          min={min}
          max={trackMax}
          step={step}
          {...props}
        />
        {rangeHint && typedMax > max && (
          <div className="mt-0.5 flex justify-between text-[10px] leading-none text-muted-foreground">
            <span>{min}{suffix}</span>
            <span title={`Need more? Type up to ${typedMax}${suffix} in the box.`}>{trackMax}{suffix}{trackMax < typedMax ? '+' : ''} (type for more)</span>
          </div>
        )}
      </div>
    </div>
  )
}

export { Slider, SliderBase }
